import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { useState } from 'react';
import { Accordion, Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import _ from 'lodash';
import {
  CreateExportRequestMutation,
  CreateExportRequestMutationVariables,
  GetUserExportsQuery,
  GetUserExportsQueryVariables,
  ProcessExportRequestMutation,
  ProcessExportRequestMutationVariables,
  StartExportJobMutation,
  StartExportJobMutationVariables,
  UserExportJobStatus,
  UserExportStatus,
} from '../../../../../../__gqltypes__';
import {
  CREATE_EXPORT_REQUEST,
  GET_SIGNED_DOWNLOAD_URL,
  GET_USER_EXPORTS,
  PROCESS_EXPORT_REQUEST,
  START_EXPORT_JOB,
} from '../../../graphql';

const COLUMNS = [
  'Request ID',
  'Requested At',
  'Processed At',
  'Status',
  'Job Status',
  'Job Started At',
  'Exported At',
  'File Expires At',
  'Manage Jobs',
  'Manage Status'
];

const UserExportDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [modalInfo] = useState<{ title: string; description: string }>({
    title: "Finish the export request",
    description: `Do you want to finish this export request?
    This operation can't be undone`,
  });
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<string>('');
  const [currentDownloadLoading, setCurrentDownloadLoading] = useState<string>();

  const [getDownloadUrl, { loading: getDownloadUrlLoading }] = useLazyQuery(GET_SIGNED_DOWNLOAD_URL, { fetchPolicy: 'no-cache' });
  const [fetchUserExports, fetchUserExportsResult] = useLazyQuery<
    GetUserExportsQuery,
    GetUserExportsQueryVariables
  >(GET_USER_EXPORTS, {
    fetchPolicy: 'no-cache',
  });
  const [startExportJob, { loading: startExportJobLoading }] = useMutation<
    StartExportJobMutation,
    StartExportJobMutationVariables
  >(START_EXPORT_JOB, {
    onCompleted: () => {
      refetchUserExports();
    }
  });
  const [createExportRequest, { loading: createExportRequestLoading }] = useMutation<
    CreateExportRequestMutation,
    CreateExportRequestMutationVariables
  >(CREATE_EXPORT_REQUEST, {
    onCompleted: () => {
      refetchUserExports();
    }
  });
  const [processExportRequest, { loading: processExportRequestLoading }] = useMutation<
    ProcessExportRequestMutation,
    ProcessExportRequestMutationVariables
  >(PROCESS_EXPORT_REQUEST, {
    onCompleted: () => {
      setShowConfirmModal(false);
      refetchUserExports();
    }
  });

  async function refetchUserExports() {
    await fetchUserExports({ variables: { input: { userId } } });
  }

  const handleDownload = async (requestId: string) => {
    setCurrentDownloadLoading(requestId);
    const { data: signedDownloadUrl } = await getDownloadUrl({ variables: { input: { requestId } } });

    if (_.isNil(signedDownloadUrl?.getSignedDownloadUrl?.url)) {
      alert('Failed to get URL');
      return;
    }

    const link = document.createElement('a');
    link.href = signedDownloadUrl.getSignedDownloadUrl.url;
    link.click();
    setCurrentDownloadLoading(undefined);
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => {
            setExpanded(!expanded);
            refetchUserExports();
          }}>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              User Export
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <div className="mb-2 d-flex justify-content-between">
                <span>The last 10 requests for current user</span>
                <div>
                  <Button
                    size="sm"
                    variant="success"
                    className="mr-2"
                    disabled={createExportRequestLoading}
                    onClick={() => {
                      createExportRequest({ variables: { input: { userId } } });
                    }}
                  >
                    <i className="fas fa-plus mr-2" />
                    Add Request Manually
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={fetchUserExportsResult.loading}
                    onClick={() => {
                      refetchUserExports();
                    }}
                  >
                    <i className="fas fa-redo-alt mr-2" />
                    Refetch
                  </Button>
                </div>
              </div>
              {(fetchUserExportsResult.loading
                || createExportRequestLoading
                || startExportJobLoading
                || processExportRequestLoading
              ) && (
                <div className="m-2">
                  <Spinner animation="border" />
                </div>
              )}
              {fetchUserExportsResult.error && (
                <div className="m-1 text-danger">{fetchUserExportsResult.error.message}</div>
              )}
              {fetchUserExportsResult.data && fetchUserExportsResult.data.userExports.userExports.length > 0 ? (
                <div className="table-responsive" style={{ maxHeight: '100vh' }}>
                  <div>
                    <Table striped bordered size="sm" id="transactionsTable">
                      <thead>
                      <tr>
                        {COLUMNS.map((column) => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                      </thead>
                      <tbody>
                      {fetchUserExportsResult.data.userExports.userExports
                        .map((userExport, index) => (
                        <tr key={index}>
                          <td key={COLUMNS[0]}>{userExport.id}</td>
                          <td key={COLUMNS[1]}>
                            {moment(userExport.requested).format('YYYY-MM-DD')}
                          </td>
                          <td
                            key={COLUMNS[2]}>{userExport.processed ? moment(userExport.processed).format('YYYY-MM-DD') : 'Not processed'}</td>
                          <td key={COLUMNS[3]}>
                            {userExport.status}
                          </td>
                          <td key={COLUMNS[4]}>{userExport.jobStatus ?? 'NOT_STARTED'}</td>
                          <td
                            key={COLUMNS[5]}>{userExport.jobStartedAt ? moment(userExport.jobStartedAt).format('YYYY-MM-DD') : 'Not started'}</td>
                          <td
                            key={COLUMNS[6]}>{userExport.exportedAt ? moment(userExport.exportedAt).format('YYYY-MM-DD') : 'Not exported'}</td>
                          <td
                            key={COLUMNS[7]}>{userExport.fileExpiresAt ? moment(userExport.fileExpiresAt).format('YYYY-MM-DD') : 'Not exported'}</td>
                          <td key={COLUMNS[8]}>
                            {userExport.jobStatus === UserExportJobStatus.DONE ? (
                                <Button
                                  size="sm"
                                  disabled={
                                    moment().isSameOrAfter(moment(userExport.fileExpiresAt))
                                    || getDownloadUrlLoading
                                  }
                                  variant="success"
                                  onClick={() => {
                                    handleDownload(userExport.id);
                                  }}
                                >
                                   { getDownloadUrlLoading && currentDownloadLoading === userExport.id ? (
                                     <Spinner animation="border" className="mr-2" size="sm" />
                                   ) : (
                                     <i className="fas fa-download mr-2" />
                                   )}
                                    Download
                                </Button>
                            ) : (
                              <Button
                                size="sm"
                                disabled={
                                  userExport.jobStatus === UserExportJobStatus.EXPORTING ||
                                  userExport.status === UserExportStatus.DONE
                                }
                                variant={userExport.jobStatus === UserExportJobStatus.FAILED ? 'danger' : 'primary'}
                                onClick={() => {
                                  startExportJob({ variables: { input: { requestId: userExport.id } } });
                                }}
                              >
                                {userExport.jobStatus === UserExportJobStatus.FAILED ? (
                                  <>
                                    <i className="fas fa-undo-alt mr-2" />
                                    Retry
                                  </>
                                ) : userExport.jobStatus === UserExportJobStatus.EXPORTING ? (
                                  <>
                                    <Spinner animation="border" className="mr-2" size="sm" />
                                    Exporting
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-file-export mr-2" />
                                    Export
                                  </>
                                )}
                              </Button>
                            )}
                          </td>
                          <td key={COLUMNS[10]}>
                            <Button
                              size="sm"
                              variant='success'
                              disabled={userExport.status === UserExportStatus.DONE
                                || userExport.jobStatus === UserExportJobStatus.EXPORTING}
                              onClick={() => {
                                setCurrentRequestId(userExport.id);
                                setShowConfirmModal(true);
                              }}
                            >
                              <i className="fa fa-check mr-2" />
                              {userExport.status === UserExportStatus.DONE ? 'Finished' : 'Done'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </Table>
                    <Modal show={showConfirmModal} centered={true}>
                      <div className="d-flex flex-column p-4 text-center">
                        <h2>{modalInfo.title}</h2>
                        <pre>{modalInfo.description}</pre>
                        <div className="d-flex justify-content-center">
                          <Button
                            size="sm"
                            variant="danger"
                            className="mr-2"
                            disabled={processExportRequestLoading}
                            onClick={() => setShowConfirmModal(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            disabled={processExportRequestLoading}
                            onClick={() => {
                              processExportRequest({ variables: { input: { requestId: currentRequestId } } });
                            }}
                          >
                            { processExportRequestLoading ? (
                              <>
                                <Spinner animation="border" className="mr-2" size="sm" />
                                <span>Processing</span>
                              </>
                            ) : (
                              <span>Finish</span>
                            ) }
                          </Button>
                        </div>
                      </div>
                    </Modal>
                  </div>
                </div>
              ) : (
                <div className="m-1">Player did not request an export</div>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default UserExportDisplay;
